import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';

export default function GlobalAlert() {
  const [queue, setQueue] = useState([]);
  const current = queue[0] || null;
  const originalAlert = useRef(null);

  const close = useCallback(() => {
    setQueue((q) => q.slice(1));
  }, []);

  useEffect(() => {
    originalAlert.current = window.alert;
    window.alert = (message) => {
      setQueue((q) => [...q, String(message)]);
    };
    return () => {
      window.alert = originalAlert.current;
    };
  }, []);

  useEffect(() => {
    if (!current) return;
    const handler = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [current, close]);

  if (!current) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={close}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl max-w-sm w-full animate-[scale-in_0.2s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 text-slate-500 hover:text-slate-300 transition-colors"
        >
          <X size={18} />
        </button>
        <div className="p-6 flex flex-col items-center text-center gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
            <Info size={24} className="text-blue-400" />
          </div>
          <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-line">{current}</p>
          <button
            onClick={close}
            autoFocus
            className="mt-1 px-8 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500"
          >
            확인
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
